import React, { useEffect, useState } from "react";
import useEscapeClose from "../../hooks/useEscapeClose";
import { isSalon } from "../../utils/businessType";

// `editEmployee` switches the modal to edit mode: the fields prefill from that
// staff member and the generated-credentials block is hidden, because the
// username is fixed at creation and passwords are reset, never edited.
function EmployeeModal({ show, onClose, onSave, editEmployee = null }) {

    const isEdit = Boolean(editEmployee);

    // A salon has an owner and receptionists only. A receptionist is stored as
    // the cashier role, which is what the till and billing permissions expect.
    const salon = isSalon();

    // Esc closes this modal (src/hooks/useEscapeClose.js).
    useEscapeClose(onClose);

    const [form, setForm] = useState({
        full_name: "",
        mobile: "",
        email: "",
        role: "cashier",
        status: "Active"
    });

    // A stylist is only a name on bills — no username or password is made.
    const noLogin = form.role === "stylist";

    const [generatedUsername, setGeneratedUsername] = useState("");
    const [generatedPassword, setGeneratedPassword] = useState("");

    const handleChange = (e) => {

        const { name, value } = e.target;

        setForm(prev => ({
            ...prev,
            [name]: value
        }));

    };

    const generateUsername = () => {

        // Username suffix = the logged-in admin's restaurant name slug
        // (e.g. "NPK Kitchen" -> "npkkitchen"), matching the backend.
        const loggedUser = JSON.parse(localStorage.getItem("user") || "{}");
        const restaurant =
            (loggedUser.restaurant_name || "restaurant")
                .toLowerCase()
                .replace(/[^a-z0-9]/g, "") || "restaurant";

        const cleanName = form.full_name
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "");

        if (!cleanName) {

            setGeneratedUsername("");

            return;

        }

        // Matches the backend: a salon's cashier logs in as "…_receptionist@…".
        const roleName = salon && form.role === "cashier" ? "receptionist" : form.role;

        setGeneratedUsername(
            `${cleanName}_${roleName}@${restaurant}`
        );

    };

    const generatePassword = () => {

        const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

        let password = "";

        for (let i = 0; i < 8; i++) {

            password += chars.charAt(
                Math.floor(Math.random() * chars.length)
            );

        }

        setGeneratedPassword(password);

    };

    const copyCredentials = () => {

        navigator.clipboard.writeText(
            `Username: ${generatedUsername}\nPassword: ${generatedPassword}`
        );

        alert("Credentials copied successfully!");

    };

    useEffect(() => {

        generateUsername();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.full_name, form.role]);

    useEffect(() => {

        if (!show) return;

        if (isEdit) {
            // Prefill from the row being edited; no credentials are generated.
            setForm({
                full_name: editEmployee.full_name || "",
                mobile: editEmployee.mobile || "",
                email: editEmployee.email || "",
                role: editEmployee.role || "cashier",
                status: editEmployee.status || "Active"
            });
        } else {
            setForm({ full_name: "", mobile: "", email: "", role: "cashier", status: "Active" });
            generatePassword();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, editEmployee]);

    const handleSubmit = (e) => {

        e.preventDefault();

        const name = form.full_name.trim();
        if (!name) { alert("Employee name is required."); return; }
        if (!/^[A-Za-z][A-Za-z .'-]*$/.test(name)) {
            alert("Name cannot contain numbers or symbols.");
            return;
        }
        if (form.mobile && !/^[0-9]{10}$/.test(form.mobile.trim())) {
            alert("Mobile number must be 10 digits.");
            return;
        }

        if (isEdit || noLogin) {
            // Editing never touches the username or the password, and a stylist
            // has neither.
            onSave({ ...form, full_name: name });
            return;
        }

        onSave({

            ...form,

            full_name: name,

            username: generatedUsername,

            password: generatedPassword

        });

    };

    if (!show) return null;

    return (

        <div className="employee-modal-overlay">

            <div className="employee-modal">

                <div className="modal-header">

                    <h2>{isEdit ? "Edit Employee" : "Add Employee"}</h2>

                    <button
                        type="button"
                        className="close-btn"
                        onClick={onClose}
                    >
                        ✕
                    </button>

                </div>

                <form onSubmit={handleSubmit}>

                    <div className="employee-form-grid">

                        <div className="form-group">

                            <label>Full Name *</label>

                            <input
                                type="text"
                                name="full_name"
                                placeholder="Enter Full Name"
                                value={form.full_name}
                                onChange={handleChange}
                                required
                            />

                        </div>

                        <div className="form-group">

                            <label>Mobile Number</label>

                            <input
                                type="text"
                                name="mobile"
                                placeholder="Enter Mobile Number"
                                value={form.mobile}
                                onChange={handleChange}
                            />

                        </div>

                        <div className="form-group">

                            <label>Email</label>

                            <input
                                type="email"
                                name="email"
                                placeholder="Enter Email"
                                value={form.email}
                                onChange={handleChange}
                            />

                        </div>

                        <div className="form-group">

                            <label>Role</label>

                            {/* A salon role is fixed once added (the backend ignores a
                                change), so editing shows it read-only. */}
                            <select
                                name="role"
                                value={form.role}
                                onChange={handleChange}
                                disabled={salon && isEdit}
                            >
                                {salon ? (
                                    <>
                                        <option value="cashier">Receptionist — can log in</option>
                                        <option value="stylist">Stylist — name only, no login</option>
                                        {/* Only shown when editing the owner's own row. */}
                                        {isEdit && form.role === "admin" && (
                                            <option value="admin">Owner</option>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <option value="admin">Admin</option>
                                        <option value="cashier">Cashier</option>
                                        <option value="waiter">Waiter</option>
                                        <option value="kitchen">Kitchen</option>
                                    </>
                                )}
                            </select>

                        </div>

                        <div className="form-group full-width">

                            <label>Status</label>

                            <select
                                name="status"
                                value={form.status}
                                onChange={handleChange}
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>

                        </div>

                    </div>

                    {/* Credentials are issued once, at creation. Editing a staff
                        member must not silently regenerate their login. */}
                    <div className="generated-box" hidden={isEdit || noLogin}>

                        <h3>Generated Login Credentials</h3>

                        <div className="credentials-grid">

                            <div>

                                <label>Username</label>

                                <input
                                    type="text"
                                    value={generatedUsername}
                                    readOnly
                                />

                            </div>

                            <div>

                                <label>Password</label>

                                <input
                                    type="text"
                                    value={generatedPassword}
                                    readOnly
                                />

                            </div>

                        </div>

                        <div className="credentials-actions">

                            <button
                                type="button"
                                className="generate-btn"
                                onClick={generatePassword}
                            >
                                🔄 Generate Again
                            </button>

                            <button
                                type="button"
                                className="copy-btn"
                                onClick={copyCredentials}
                            >
                                📋 Copy Credentials
                            </button>

                        </div>

                    </div>

                    <div className="modal-buttons">

                        <button
                            type="button"
                            className="cancel-btn"
                            onClick={onClose}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="save-btn"
                        >
                            {isEdit ? "Save Changes" : noLogin ? "Add Stylist" : "Create Employee"}
                        </button>

                    </div>

                </form>

            </div>

        </div>

    );

}

export default EmployeeModal;